/**
 * The "position on the map" section is deliberately NOT another gap confession: it shows
 * that the author knows what a production FDS is made of, that each missing piece is a
 * conditional deferral tied to a recorded decision (ADR-004/020/023), and that one layer
 * (certificate + governor) is implemented here despite being rare in production.
 *
 * Sources for the "production components" column were actually fetched and read before
 * being cited (project rule: never cite unread sources): the Kakaobank tech blog post on
 * applying AI to their FDS (Kafka event stream, Redis online feature store, separate ML
 * serving — 2023-10) and the FSS/FSI joint FDS operation guideline announcement
 * (detection rules + response procedures, 2023-10, via KDI's policy archive). No claim
 * about any specific bank beyond what those public posts state; wording stays at the
 * "실무에서는 일반적으로" level. The applicant's own banking career is deliberately NOT
 * mentioned here — the site argues from project evidence only.
 */
export function Limitations() {
  return (
    <>
      <p>
        이 페이지는 <strong>이 프로젝트가 하지 못하는 것</strong>을 적어둔 곳이에요. 시험
        성적표에서 100점짜리 과목만 보여주는 사람을 믿을 수 있나요? 이 프로젝트의 증명
        방식은 반대입니다 — <strong>불합격한 시험지까지 전부 공개하고, 그 대신 공개한 것은
        전부 재현 가능하게.</strong> 아래 목록이 길수록 위 탭들의 숫자를 더 믿을 수 있어요.
      </p>

      <section className="card">
        <h2>😅 솔직하게 말하면</h2>
        <ul className="plain">
          <li>
            <strong>실제 은행 데이터가 아닙니다.</strong> 이 데모의 모든 거래는 서버가 만든 합성
            값이고, 모든 성능 수치는 자체 제작한 합성 데이터 기준입니다. 실제 은행에서의 성능을
            뜻하지 않습니다.
          </li>
          <li>
            기본으로 돌아가는 Predictor는 학습된 ML 모델이 아니라{" "}
            <strong>결정론적 규칙 기반 스코어러</strong>입니다. 학습 모델(Logistic Regression)을
            만들어 비교했지만, <strong>사전에 정한 합격 기준을 통과하지 못해 투입하지
            않았습니다</strong> — 그 불합격 성적표도 &quot;직접 해보기&quot; 탭에 그대로
            공개되어 있어요.
          </li>
          <li>
            &quot;본인 확인(STEP_UP)&quot;의 고객 통과율은 실측이 아니라 <strong>가정값</strong>
            입니다. 비관·낙관 가정으로도 전부 다시 계산해 결론이 버티는지 확인했지만, 가정은
            가정입니다.
          </li>
          <li>
            사람이 검토하는 대기열(REVIEW)을 처리하는 업무 화면은 만들지 않았습니다 — 이
            프로젝트는 &quot;누구에게 넘길지 판정하는 시스템&quot;까지입니다.
          </li>
          <li>
            대규모 트래픽 부하 실험은 하지 않았습니다. 단일 서버 데모 기준입니다.
          </li>
          <li>
            NVIDIA Triton 서빙 실험(&quot;어떻게 만들었나&quot; 5막)의 결론은{" "}
            <strong>CPU 한 대·4KB 소형 모델 조건</strong>에서의 측정입니다. GPU나 대형
            모델에서는 결론이 뒤집힐 수 있고, 그래서 &quot;영원히 안 씀&quot;이 아니라{" "}
            <strong>재도입 조건을 적어둔 기각</strong>입니다.
          </li>
          <li>
            Rate limit은 단일 인스턴스 in-memory 구현입니다 (서버를 여러 대로 늘리면 별도 공유
            저장소가 필요합니다).
          </li>
        </ul>
      </section>

      <section className="card">
        <h2>🗺️ 실무 FDS 지도에서 SafeFDS의 위치</h2>
        <p>
          위 목록은 &quot;못 하는 것&quot;이었고, 이 표는 <strong>지도 위의 위치
          표시</strong>입니다. 실무 FDS가 일반적으로 어떤 부품으로 이루어지는지
          알고 있고<sup>1) 2)</sup>, 이 프로젝트에 없는 부품은 각각{" "}
          <strong>이유와 도입 조건을 적어둔 조건부 보류</strong>이며, 반대로 실무에서도
          드문 부품 하나는 여기에 구현되어 있다는 것 — 이 세 가지를 한 표로 보여드려요.
        </p>
        <table className="evidence-table">
          <thead>
            <tr>
              <th>실무 FDS 구성요소</th>
              <th>SafeFDS 현재</th>
              <th>선택 이유와 도입 조건</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>이벤트 스트림 (Kafka 등)</td>
              <td>
                Outbox 패턴 구현 — 판정과 이벤트 기록을 <strong>한 트랜잭션</strong>에
                저장. 발행 Worker·브로커는 미구현
              </td>
              <td>
                이벤트를 실제로 소비할 컴포넌트가 아직 없음 — 소비자가 생기면 Worker부터
                도입 (ADR-004)
              </td>
            </tr>
            <tr>
              <td>Feature Store (Redis 이력 피처)</td>
              <td>요청 한 건에서 그 자리에서 계산(JIT)만 사용</td>
              <td>
                캐시는 신선도·정합성·장애 경로 복잡도를 추가하므로 보류, 피처 계산이 지연
                예산을 넘으면 도입 (ADR-020). 부수 효과: <strong>이력 피처를 학습에도
                넣지 않아 학습-서빙 불일치(Training-Serving Skew)가 구조적으로
                차단됨</strong>
              </td>
            </tr>
            <tr>
              <td>ML 서빙 계층 (별도 모델 서버)</td>
              <td>인프로세스 추론 + 안전장치 5종 내장 (&quot;어떻게 만들었나&quot; 5막)</td>
              <td>
                별도 서버(Triton)를 실제 설치·실측한 뒤 기각 — 이 모델 크기에서는 네트워크
                왕복만 손해. 모델 대형화·다중 모델 시 재도입 (ADR-023)
              </td>
            </tr>
            <tr>
              <td>검토 조직·케이스 관리</td>
              <td>데모 수준 검토 큐 (라이브 엔진이 실시간 채점, 처리 기록은 추가 전용)</td>
              <td>
                검토 조직의 업무 시스템은 1인 PoC 범위 밖 — 이 시스템의 경계는
                &quot;누구에게 넘길지 판정&quot;까지
              </td>
            </tr>
            <tr>
              <td>자동화 권한 통제 (인증서 + 런타임 거버너)</td>
              <td>
                <strong>구현됨</strong> — 체크섬 봉인 인증서 + 허용선 위반 시 자동 강등
              </td>
              <td>
                실무 FDS에서도 드문 계층 — &quot;언제 믿어도 되는가&quot;를 증명하려는
                이 프로젝트의 기여
              </td>
            </tr>
          </tbody>
        </table>
        <p className="muted">
          1) 실무 구성 참고: 카카오뱅크 기술블로그,{" "}
          <a
            href="https://tech.kakaobank.com/posts/2310-applying-ai-into-fds-system/"
            target="_blank"
            rel="noreferrer"
          >
            FDS 시스템에 AI 적용하기
          </a>{" "}
          (2023) — 이벤트 스트림(Kafka)·Online Feature Store(Redis)·별도 ML 서빙 구성이
          소개됨. 2) 금융감독원·금융보안원,{" "}
          <a
            href="https://eiec.kdi.re.kr/policy/materialView.do?num=243367"
            target="_blank"
            rel="noreferrer"
          >
            「이상금융거래탐지시스템(FDS) 운영 가이드라인」 마련
          </a>{" "}
          (2023) — 탐지룰과 대응 절차 등 운영 요건. 특정 회사에 대한 평가가 아니라
          &quot;실무에서는 일반적으로 이런 부품을 쓴다&quot;는 근거로만 인용합니다.
        </p>
      </section>

      <section className="card">
        <h2>🚀 다음에 할 수 있는 것</h2>
        <ul className="plain">
          <li>본인 확인(STEP_UP) 흐름을 공개 데모에서 켜기 — 코드·안전장치는 준비 완료</li>
          <li>
            사람이 검토하는 건을 돕는 <strong>비동기 검토 도우미(REVIEW Copilot)</strong> —
            설계 문서까지 완료, 구현은 다음 단계
          </li>
          <li>대량 트래픽 부하 실험으로 처리량 한계 측정하기 (지연 측정은 완료, 부하는 미수행)</li>
        </ul>
      </section>
    </>
  );
}
